import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { GlassCard, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Target, Lock, Unlock, CheckCircle, AlertTriangle, Plus, X,
  GraduationCap, Wrench, Shield, Rocket, Briefcase, TrendingUp,
  DollarSign, Clock, Award, ChevronRight, Edit2, Trash2, Star,
  BookOpen, Building, Code, Stethoscope, Scale, FlaskConical, Search
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import CollegeSearchSelector from './CollegeSearchSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Path configurations for HIGH SCHOOL students
const HIGH_SCHOOL_PATH_TYPES = [
  { id: 'college', name: 'College', icon: GraduationCap, color: 'blue', description: '4-year university or college' },
  { id: 'trade', name: 'Trade', icon: Wrench, color: 'amber', description: 'Vocational training & certifications' },
  { id: 'military', name: 'Military', icon: Shield, color: 'green', description: 'Armed forces service' },
  { id: 'entrepreneurship', name: 'Startup', icon: Rocket, color: 'purple', description: 'Start your own business' },
  { id: 'workforce', name: 'Workforce', icon: Briefcase, color: 'slate', description: 'Enter the job market directly' },
  { id: 'nc_district', name: 'NC District', icon: Building, color: 'cyan', description: 'NC K-12 school district student', locked: true },
];

// Path configurations for COLLEGE students
const COLLEGE_PATH_TYPES = [
  { id: 'graduate_school', name: 'Graduate School', icon: BookOpen, color: 'purple', description: "Master's, PhD, or professional degree" },
  { id: 'career_entry', name: 'Career Entry', icon: Briefcase, color: 'blue', description: 'Enter your target industry' },
  { id: 'professional_school', name: 'Professional School', icon: Scale, color: 'amber', description: 'Law, Medical, or MBA programs' },
  { id: 'research', name: 'Research', icon: FlaskConical, color: 'cyan', description: 'Research positions or fellowships' },
  { id: 'entrepreneurship', name: 'Entrepreneurship', icon: Rocket, color: 'pink', description: 'Start your own venture' },
  { id: 'gap_year', name: 'Gap Year', icon: TrendingUp, color: 'green', description: 'Travel, volunteer, or explore' },
];

// Path configurations for CAREER PROFESSIONALS
const CAREER_PRO_PATH_TYPES = [
  { id: 'career_pivot', name: 'Career Pivot', icon: TrendingUp, color: 'cyan', description: 'Transition to a new field' },
  { id: 'advancement', name: 'Advancement', icon: Award, color: 'blue', description: 'Move up in current field' },
  { id: 'entrepreneurship', name: 'Entrepreneurship', icon: Rocket, color: 'purple', description: 'Start your own business' },
  { id: 'consulting', name: 'Consulting', icon: Building, color: 'amber', description: 'Independent consulting work' },
  { id: 'education', name: 'Further Education', icon: GraduationCap, color: 'green', description: 'Degrees or certifications' },
  { id: 'freelance', name: 'Freelance/Contract', icon: Code, color: 'pink', description: 'Independent contract work' },
];

// Legacy export for backward compatibility
const PATH_TYPES = HIGH_SCHOOL_PATH_TYPES;

const LOCK_STATUSES = [
  { id: 'exploring', label: 'Exploring', icon: Target, color: 'slate', description: 'Still researching options' },
  { id: 'soft_lock', label: 'Soft Lock', icon: Unlock, color: 'yellow', description: 'Seriously considering' },
  { id: 'hard_lock', label: 'Hard Lock', icon: Lock, color: 'blue', description: 'Committed to this path' },
  { id: 'finalized', label: 'Finalized', icon: CheckCircle, color: 'emerald', description: 'Decision made, enrolled/accepted' },
];

// Format currency
const formatCurrency = (amount) => {
  if (!amount) return '$0';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount);
};

// Path Selection Card
const PathCard = ({ path, pathTypes = PATH_TYPES, onEdit, onLockChange, onDelete }) => {
  // Look in provided pathTypes first, then fallback to all path type arrays
  const pathType = pathTypes.find(p => p.id === path.path_type) || 
                   HIGH_SCHOOL_PATH_TYPES.find(p => p.id === path.path_type) ||
                   COLLEGE_PATH_TYPES.find(p => p.id === path.path_type) ||
                   CAREER_PRO_PATH_TYPES.find(p => p.id === path.path_type);
  const lockStatus = LOCK_STATUSES.find(s => s.id === path.lock_status);
  const PathIcon = pathType?.icon || Target;
  const LockIcon = lockStatus?.icon || Target;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-xl border-2 transition-all ${
        path.lock_status === 'finalized' ? 'border-emerald-500 bg-emerald-500/10' :
        path.lock_status === 'hard_lock' ? 'border-blue-500 bg-blue-500/10' :
        path.lock_status === 'soft_lock' ? 'border-yellow-500/50 bg-yellow-500/5' :
        'border-slate-700 bg-slate-800/50'
      }`}
    >
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl bg-${pathType?.color || 'slate'}-500/20 flex items-center justify-center`}>
              <PathIcon className={`w-6 h-6 text-${pathType?.color || 'slate'}-400`} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-white font-bold text-lg">{path.institution_name}</span>
                {path.rank === 1 && <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />}
              </div>
              <p className="text-slate-300 text-sm">{pathType?.name} • {path.major_or_track || 'General'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => onEdit(path)} className="text-slate-300">
              <Edit2 className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onDelete(path.id)} className="text-red-400 hover:text-red-300">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        {/* Cost Summary */}
        {path.cost_profile && (
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="p-3 rounded-lg bg-slate-900/50 text-center">
              <DollarSign className="w-4 h-4 text-blue-400 mx-auto mb-1" />
              <p className="text-white font-semibold text-sm">{formatCurrency(path.cost_profile.total_program_cost)}</p>
              <p className="text-slate-300 text-xs">Total Cost</p>
            </div>
            <div className="p-3 rounded-lg bg-slate-900/50 text-center">
              <TrendingUp className="w-4 h-4 text-emerald-400 mx-auto mb-1" />
              <p className="text-white font-semibold text-sm">{formatCurrency(path.cost_profile.starting_salary)}</p>
              <p className="text-slate-300 text-xs">Starting Salary</p>
            </div>
            <div className="p-3 rounded-lg bg-slate-900/50 text-center">
              <Clock className="w-4 h-4 text-purple-400 mx-auto mb-1" />
              <p className="text-white font-semibold text-sm">{path.cost_profile.break_even_years?.toFixed(1)} yrs</p>
              <p className="text-slate-300 text-xs">Break-even</p>
            </div>
          </div>
        )}
        
        {/* Lock Status */}
        <div className="flex items-center justify-between">
          <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2 bg-${lockStatus?.color || 'slate'}-500/20 text-${lockStatus?.color || 'slate'}-400`}>
            <LockIcon className="w-4 h-4" />
            {lockStatus?.label}
          </span>
          
          {path.lock_status !== 'finalized' && (
            <div className="flex gap-2">
              {path.lock_status === 'exploring' && (
                <Button size="sm" onClick={() => onLockChange(path.id, 'soft_lock')} className="bg-yellow-600 hover:bg-yellow-700">
                  Soft Lock
                </Button>
              )}
              {path.lock_status === 'soft_lock' && (
                <Button size="sm" onClick={() => onLockChange(path.id, 'hard_lock')} className="bg-blue-600 hover:bg-blue-700">
                  Hard Lock
                </Button>
              )}
              {path.lock_status === 'hard_lock' && (
                <Button size="sm" onClick={() => onLockChange(path.id, 'finalized')} className="bg-emerald-600 hover:bg-emerald-700">
                  Finalize
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Path-specific form configurations
const PATH_FORM_CONFIG = {
  college: {
    institutionLabel: 'College / University Name',
    institutionPlaceholder: 'e.g., Duke University, MIT, State University',
    majorLabel: 'Major / Field of Study',
    majorPlaceholder: 'e.g., Computer Science, Business, Engineering',
    defaultDuration: 4,
    useCollegeSearch: true
  },
  trade: {
    institutionLabel: 'Trade School / Training Program',
    institutionPlaceholder: 'e.g., Lincoln Tech, Local HVAC Training, Electrician Apprenticeship',
    majorLabel: 'Trade / Specialty',
    majorPlaceholder: 'e.g., HVAC, Electrical, Plumbing, Welding, Auto Mechanic',
    defaultDuration: 2,
    useCollegeSearch: false
  },
  military: {
    institutionLabel: 'Branch of Service',
    institutionPlaceholder: 'e.g., U.S. Army, Navy, Air Force, Marines, Coast Guard',
    majorLabel: 'MOS / Rating / Career Field',
    majorPlaceholder: 'e.g., Infantry, Aviation, IT, Medical, Intelligence',
    defaultDuration: 4,
    useCollegeSearch: false
  },
  entrepreneurship: {
    institutionLabel: 'Business / Startup Name',
    institutionPlaceholder: 'e.g., My Tech Startup, Local Service Business',
    majorLabel: 'Industry / Sector',
    majorPlaceholder: 'e.g., Technology, Food Service, E-commerce, Consulting',
    defaultDuration: 1,
    useCollegeSearch: false
  },
  workforce: {
    institutionLabel: 'Company / Employer',
    institutionPlaceholder: 'e.g., Target Company, Industry Sector',
    majorLabel: 'Job Role / Position',
    majorPlaceholder: 'e.g., Sales Associate, Customer Service, Entry-level IT',
    defaultDuration: 1,
    useCollegeSearch: false
  },
  graduate_school: {
    institutionLabel: 'Graduate School / University',
    institutionPlaceholder: 'e.g., Stanford, Harvard, State University Graduate Program',
    majorLabel: 'Degree Program',
    majorPlaceholder: 'e.g., MBA, PhD in Physics, Masters in CS',
    defaultDuration: 2,
    useCollegeSearch: false
  },
  career_entry: {
    institutionLabel: 'Target Company / Industry',
    institutionPlaceholder: 'e.g., Google, Finance Industry, Healthcare Sector',
    majorLabel: 'Target Role',
    majorPlaceholder: 'e.g., Software Engineer, Analyst, Marketing Manager',
    defaultDuration: 1,
    useCollegeSearch: false
  },
  professional_school: {
    institutionLabel: 'Professional School',
    institutionPlaceholder: 'e.g., Harvard Law, Johns Hopkins Medical, Wharton MBA',
    majorLabel: 'Program Type',
    majorPlaceholder: 'e.g., JD, MD, MBA, PharmD',
    defaultDuration: 3,
    useCollegeSearch: false
  },
  research: {
    institutionLabel: 'Research Institution / Lab',
    institutionPlaceholder: 'e.g., NIH, MIT Lincoln Lab, University Research Center',
    majorLabel: 'Research Focus',
    majorPlaceholder: 'e.g., Biomedical Research, AI/ML, Climate Science',
    defaultDuration: 2,
    useCollegeSearch: false
  },
  gap_year: {
    institutionLabel: 'Program / Activity',
    institutionPlaceholder: 'e.g., Peace Corps, Travel, AmeriCorps, Work & Travel',
    majorLabel: 'Focus Area',
    majorPlaceholder: 'e.g., Community Service, Cultural Immersion, Skill Building',
    defaultDuration: 1,
    useCollegeSearch: false
  },
  career_pivot: {
    institutionLabel: 'Target Industry / Company',
    institutionPlaceholder: 'e.g., Tech Industry, Healthcare, Finance Sector',
    majorLabel: 'New Career Direction',
    majorPlaceholder: 'e.g., Data Science, UX Design, Product Management',
    defaultDuration: 1,
    useCollegeSearch: false
  },
  advancement: {
    institutionLabel: 'Current Company / Target',
    institutionPlaceholder: 'e.g., Current Employer, Target Company for Promotion',
    majorLabel: 'Target Position',
    majorPlaceholder: 'e.g., Senior Manager, Director, VP',
    defaultDuration: 2,
    useCollegeSearch: false
  },
  consulting: {
    institutionLabel: 'Consulting Focus / Firm',
    institutionPlaceholder: 'e.g., Independent Consultant, McKinsey, Deloitte',
    majorLabel: 'Specialty Area',
    majorPlaceholder: 'e.g., Strategy, Technology, Operations, HR',
    defaultDuration: 1,
    useCollegeSearch: false
  },
  education: {
    institutionLabel: 'School / Program',
    institutionPlaceholder: 'e.g., Executive MBA Program, Professional Certificate',
    majorLabel: 'Degree / Certification',
    majorPlaceholder: 'e.g., Executive MBA, PMP, AWS Certification',
    defaultDuration: 2,
    useCollegeSearch: false
  },
  freelance: {
    institutionLabel: 'Freelance Business Name',
    institutionPlaceholder: 'e.g., Your Freelance Brand, Contract Work',
    majorLabel: 'Service Offering',
    majorPlaceholder: 'e.g., Web Development, Graphic Design, Writing',
    defaultDuration: 1,
    useCollegeSearch: false
  }
};

// Add Path Modal
const AddPathModal = ({ isOpen, onClose, onSubmit, editingPath = null, pathTypes = PATH_TYPES }) => {
  const defaultPathType = pathTypes[0]?.id || 'college';
  const [formData, setFormData] = useState({
    path_type: defaultPathType,
    institution_name: '',
    major_or_track: '',
    enrollment_year: new Date().getFullYear() + 1,
    duration_years: 4,
    lock_status: 'exploring',
    rank: 1
  });
  
  // Get form configuration for selected path type
  const formConfig = PATH_FORM_CONFIG[formData.path_type] || PATH_FORM_CONFIG.college;
  
  useEffect(() => {
    if (editingPath) {
      setFormData({
        path_type: editingPath.path_type || defaultPathType,
        institution_name: editingPath.institution_name || '',
        major_or_track: editingPath.major_or_track || '',
        enrollment_year: editingPath.enrollment_year || new Date().getFullYear() + 1,
        duration_years: editingPath.duration_years || formConfig.defaultDuration,
        lock_status: editingPath.lock_status || 'exploring',
        rank: editingPath.rank || 1
      });
    } else {
      setFormData({
        path_type: defaultPathType,
        institution_name: '',
        major_or_track: '',
        enrollment_year: new Date().getFullYear() + 1,
        duration_years: formConfig.defaultDuration,
        lock_status: 'exploring',
        rank: 1
      });
    }
  }, [editingPath, isOpen, defaultPathType]);
  
  // Update duration when path type changes
  useEffect(() => {
    const config = PATH_FORM_CONFIG[formData.path_type] || PATH_FORM_CONFIG.college;
    if (!editingPath) {
      setFormData(prev => ({
        ...prev,
        duration_years: config.defaultDuration
      }));
    }
  }, [formData.path_type]);
  
  if (!isOpen) return null;
  
  // Use responsive grid that wraps better for longer names
  const gridCols = pathTypes.length <= 3 ? 'grid-cols-3' : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-5';
  
  // Check if we should use college search
  const useCollegeSearch = formConfig.useCollegeSearch && !editingPath;
  
  return (
    <div className="fixed inset-0 bg-[#0a1729]/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">
              {editingPath ? 'Edit Path' : 'Add New Path'}
            </h2>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Path Type */}
          <div>
            <Label className="text-white mb-3 block">Path Type</Label>
            <div className={`grid ${gridCols} gap-2`}>
              {pathTypes.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.id}
                    onClick={() => setFormData(prev => ({ ...prev, path_type: type.id, institution_name: '', major_or_track: '' }))}
                    className={`p-3 rounded-xl border text-center transition-all ${
                      formData.path_type === type.id
                        ? `border-${type.color}-500 bg-${type.color}-500/20`
                        : 'border-slate-700 hover:border-slate-600'
                    }`}
                    data-testid={`path-type-${type.id}`}
                  >
                    <Icon className={`w-6 h-6 mx-auto mb-1 ${
                      formData.path_type === type.id ? `text-${type.color}-400` : 'text-slate-300'
                    }`} />
                    <p className="text-xs text-slate-300">{type.name}</p>
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* College Search - Show when college path type is selected and not editing */}
          {useCollegeSearch ? (
            <div className="border-t border-slate-700 pt-4">
              <CollegeSearchSelector 
                onSelect={(path) => {
                  onSubmit(path);
                  onClose();
                }}
                rank={formData.rank}
                showLockOptions={true}
              />
            </div>
          ) : (
            <>
              {/* Institution - with path-specific labels */}
              <div>
                <Label className="text-white mb-2 block">{formConfig.institutionLabel}</Label>
                <Input
                  value={formData.institution_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, institution_name: e.target.value }))}
                  placeholder={formConfig.institutionPlaceholder}
                  className="bg-slate-800 border-slate-700"
                  data-testid="path-institution-input"
                />
              </div>
          
              {/* Major/Track - with path-specific labels */}
              <div>
                <Label className="text-white mb-2 block">{formConfig.majorLabel}</Label>
                <Input
                  value={formData.major_or_track}
                  onChange={(e) => setFormData(prev => ({ ...prev, major_or_track: e.target.value }))}
                  placeholder={formConfig.majorPlaceholder}
                  className="bg-slate-800 border-slate-700"
                  data-testid="path-major-input"
                />
              </div>
          
              {/* Year & Duration */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white mb-2 block">Start Year</Label>
                  <Input
                    type="number"
                    value={formData.enrollment_year}
                    onChange={(e) => setFormData(prev => ({ ...prev, enrollment_year: parseInt(e.target.value) }))}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div>
                  <Label className="text-white mb-2 block">Duration (Years)</Label>
                  <Input
                    type="number"
                    value={formData.duration_years}
                    onChange={(e) => setFormData(prev => ({ ...prev, duration_years: parseInt(e.target.value) }))}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              </div>
          
              {/* Rank */}
              <div>
                <Label className="text-white mb-2 block">Priority Rank</Label>
                <div className="flex gap-2">
                  {[1, 2, 3].map((rank) => (
                    <button
                      key={rank}
                      onClick={() => setFormData(prev => ({ ...prev, rank }))}
                      className={`flex-1 p-3 rounded-lg border text-center transition-all ${
                        formData.rank === rank
                          ? 'border-yellow-500 bg-yellow-500/20'
                          : 'border-slate-700 hover:border-slate-600'
                      }`}
                    >
                      <span className="text-white font-semibold">#{rank}</span>
                      <p className="text-xs text-slate-300">
                        {rank === 1 ? 'Top Choice' : rank === 2 ? 'Backup' : 'Safety'}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
          
              {/* Lock Status */}
              <div>
                <Label className="text-white mb-2 block">Commitment Level</Label>
                <div className="grid grid-cols-2 gap-2">
                  {LOCK_STATUSES.map((status) => {
                    const Icon = status.icon;
                    return (
                      <button
                        key={status.id}
                        onClick={() => setFormData(prev => ({ ...prev, lock_status: status.id }))}
                        className={`p-3 rounded-lg border text-left transition-all ${
                          formData.lock_status === status.id
                            ? `border-${status.color}-500 bg-${status.color}-500/20`
                            : 'border-slate-700 hover:border-slate-600'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className={`w-4 h-4 text-${status.color}-400`} />
                          <span className="text-white font-medium text-sm">{status.label}</span>
                        </div>
                        <p className="text-xs text-slate-300">{status.description}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
        
        {/* Footer - show for all non-college paths or when editing */}
        {!useCollegeSearch && (
          <div className="p-6 border-t border-slate-800 flex justify-end gap-3">
            <Button variant="outline" onClick={onClose} className="border-slate-600">Cancel</Button>
            <Button
              onClick={() => {
                onSubmit(formData);
                onClose();
              }}
              disabled={!formData.institution_name}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="submit-path-btn"
            >
              {editingPath ? 'Update Path' : 'Add Path'}
            </Button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

// Helper function to get path types based on user type
const getPathTypesForUser = (user) => {
  const isCollegeStudent = user?.education_level === 'some_college' || 
                          user?.education_level === 'bachelor' || 
                          user?.education_level === 'graduate' ||
                          (user?.age && user.age >= 18 && user?.role === 'student');
  const isCareerProfessional = user?.role === 'career_professional' || 
                               user?.role === 'career_changer' || 
                               user?.role === 'professional';
  
  if (isCareerProfessional) return CAREER_PRO_PATH_TYPES;
  if (isCollegeStudent) return COLLEGE_PATH_TYPES;
  return HIGH_SCHOOL_PATH_TYPES;
};

// Main Component
export default function PathLockSelector({ compact = false }) {
  const { user, getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [paths, setPaths] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPath, setEditingPath] = useState(null);
  
  // Get appropriate path types for this user
  const userPathTypes = getPathTypesForUser(user);
  
  // Determine user type for labels
  const isCollegeStudent = user?.education_level === 'some_college' || 
                          user?.education_level === 'bachelor' || 
                          user?.education_level === 'graduate' ||
                          (user?.age && user.age >= 18 && user?.role === 'student');
  const isCareerProfessional = user?.role === 'career_professional' || 
                               user?.role === 'career_changer' || 
                               user?.role === 'professional';
  
  // Get appropriate title
  const getTitle = () => {
    if (isCareerProfessional) return "My Career Goals";
    if (isCollegeStudent) return "My Future Paths";
    return "My Career Paths";
  };
  
  useEffect(() => {
    fetchPaths();
  }, []);
  
  const fetchPaths = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/paths/my-paths`, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setPaths(response.data.paths || []);
    } catch (error) {
      console.error('Failed to fetch paths:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddPath = async (pathData) => {
    try {
      await axios.post(`${API}/paths/select`, pathData, {
        ...getAuthHeader(),
        withCredentials: true
      });
      toast.success('Path added successfully!');
      fetchPaths();
    } catch (error) {
      toast.error('Failed to add path');
    }
  };
  
  const handleLockChange = async (pathId, newStatus) => {
    try {
      await axios.put(`${API}/paths/${pathId}/lock?lock_status=${newStatus}`, {}, {
        ...getAuthHeader(),
        withCredentials: true
      });
      toast.success(`Path status updated to ${newStatus.replace('_', ' ')}`);
      fetchPaths();
    } catch (error) {
      toast.error('Failed to update lock status');
    }
  };
  
  const handleDelete = async (pathId) => {
    if (!confirm('Are you sure you want to remove this path?')) return;
    
    try {
      await axios.delete(`${API}/paths/${pathId}`, {
        ...getAuthHeader(),
        withCredentials: true
      });
      toast.success('Path removed');
      fetchPaths();
    } catch (error) {
      toast.error('Failed to remove path');
    }
  };
  
  if (loading) {
    return (
      <GlassCard className="p-8 text-center">
        <LoadingSpinner size="lg" />
      </GlassCard>
    );
  }
  
  // Compact mode for dashboard widget
  if (compact) {
    return (
      <div className="space-y-4" data-testid="path-lock-compact">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Target className="w-5 h-5 text-blue-400" />
            {getTitle()}
          </h3>
          {paths.length < 3 && (
            <Button 
              size="sm" 
              onClick={() => { setEditingPath(null); setShowAddModal(true); }} 
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="add-path-btn-compact"
            >
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          )}
        </div>
        
        {paths.length > 0 ? (
          <div className="space-y-2">
            {paths.sort((a, b) => a.rank - b.rank).slice(0, 3).map((path) => {
              const pathType = userPathTypes.find(p => p.id === path.path_type) || PATH_TYPES.find(p => p.id === path.path_type);
              const lockStatus = LOCK_STATUSES.find(s => s.id === path.lock_status);
              const PathIcon = pathType?.icon || Target;
              const LockIcon = lockStatus?.icon || Target;
              
              return (
                <div 
                  key={path.id} 
                  className={`p-3 rounded-lg border transition-all ${
                    path.lock_status === 'finalized' ? 'border-emerald-500/50 bg-emerald-500/10' :
                    path.lock_status === 'hard_lock' ? 'border-blue-500/50 bg-blue-500/10' :
                    path.lock_status === 'soft_lock' ? 'border-yellow-500/30 bg-yellow-500/5' :
                    'border-slate-700 bg-slate-800/30'
                  }`}
                  data-testid={`path-card-${path.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <PathIcon className="w-4 h-4 text-blue-400" />
                      <span className="text-white font-medium text-sm truncate max-w-[150px]">
                        {path.institution_name}
                      </span>
                      {path.rank === 1 && <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />}
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 ${
                      path.lock_status === 'finalized' ? 'bg-emerald-500/20 text-emerald-400' :
                      path.lock_status === 'hard_lock' ? 'bg-blue-500/20 text-blue-400' :
                      path.lock_status === 'soft_lock' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-slate-700 text-slate-300'
                    }`}>
                      <LockIcon className="w-3 h-3" />
                      {lockStatus?.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-6 text-slate-300">
            <Target className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No paths selected yet</p>
            <Button 
              size="sm" 
              onClick={() => setShowAddModal(true)} 
              className="mt-2 bg-blue-600 hover:bg-blue-700"
              data-testid="add-first-path-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add Path
            </Button>
          </div>
        )}
        
        {/* Modal */}
        <AddPathModal
          isOpen={showAddModal}
          onClose={() => { setShowAddModal(false); setEditingPath(null); }}
          onSubmit={handleAddPath}
          editingPath={editingPath}
          pathTypes={userPathTypes}
        />
      </div>
    );
  }
  
  // Full mode
  
  return (
    <div className="space-y-6" data-testid="path-lock-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Target className="w-6 h-6 text-blue-400" />
            {getTitle()}
          </h2>
          <p className="text-slate-300 text-sm mt-1">Select and lock your top 3 path choices</p>
        </div>
        {paths.length < 3 && (
          <Button onClick={() => { setEditingPath(null); setShowAddModal(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="add-path-btn-full">
            <Plus className="w-4 h-4 mr-2" /> Add Path
          </Button>
        )}
      </div>
      
      {/* Progress */}
      {paths.length > 0 && (
        <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-800/50">
          <div className="flex-1">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-slate-300">Path Selection Progress</span>
              <span className="text-white">{paths.length}/3 paths selected</span>
            </div>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all"
                style={{ width: `${(paths.length / 3) * 100}%` }}
              />
            </div>
          </div>
          {paths.some(p => p.lock_status === 'hard_lock' || p.lock_status === 'finalized') && (
            <div className="flex items-center gap-2 text-emerald-400">
              <CheckCircle className="w-5 h-5" />
              <span className="text-sm font-medium">Path Locked!</span>
            </div>
          )}
        </div>
      )}
      
      {/* Paths Grid */}
      {paths.length > 0 ? (
        <div className="grid gap-4">
          {paths.sort((a, b) => a.rank - b.rank).map((path) => (
            <PathCard
              key={path.id}
              path={path}
              pathTypes={userPathTypes}
              onEdit={(p) => { setEditingPath(p); setShowAddModal(true); }}
              onLockChange={handleLockChange}
              onDelete={handleDelete}
            />
          ))}
        </div>
      ) : (
        <GlassCard className="p-12 text-center">
          <Target className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">No Paths Selected Yet</h3>
          <p className="text-slate-300 mb-6 max-w-md mx-auto">
            Start exploring your future by adding up to 3 career paths. You can lock in your choices as you become more certain.
          </p>
          <Button onClick={() => setShowAddModal(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="add-first-path-btn-full">
            <Plus className="w-4 h-4 mr-2" /> Add Your First Path
          </Button>
        </GlassCard>
      )}
      
      {/* Info Card */}
      <GlassCard className="p-4 border-blue-500/30 bg-blue-500/5">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-blue-400 font-medium text-sm">How Path Locking Works</p>
            <p className="text-slate-300 text-sm mt-1">
              <strong>Exploring:</strong> Just researching • 
              <strong> Soft Lock:</strong> Seriously considering, notifies your family • 
              <strong> Hard Lock:</strong> Committed, triggers financial planning • 
              <strong> Finalized:</strong> Enrolled/accepted, locked in
            </p>
          </div>
        </div>
      </GlassCard>
      
      {/* Modal */}
      <AddPathModal
        isOpen={showAddModal}
        onClose={() => { setShowAddModal(false); setEditingPath(null); }}
        onSubmit={handleAddPath}
        editingPath={editingPath}
        pathTypes={userPathTypes}
      />
    </div>
  );
}
